import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { FETCH_LOCATION_SELECTED_IT_APPS, FETCH_SELECTED_IT_APPS } from './queries';

type AffectedApplication = {
  name: string;
};

type ITApplicationToRemove = { uuid: string }[];

type ITApplication = {
  uuid: string;
  application: string;
  applicationType: string;
  supportsRevenue: boolean;
  percentage: number;
}

type UseFetchSelectedITApplicationsResult<T> = {
  getSelectedItApplications: T[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<T>;
}

/**
 * Hook to fetch the selected IT applications for the enterprise level
 * @returns The selected IT applications for the enterprise level
*/
export const useFetchSelectedITApplications = () => {
  const { data, loading, error, refetch } = useQuery<UseFetchSelectedITApplicationsResult<ITApplication>>(FETCH_SELECTED_IT_APPS);

  return {
    data: data,
    loading,
    error,
    refetch,
  };
}

type UseFetchSelectedLocationITApplicationsResult<T> = {
  getSelectedLocationItApplications: T[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<T>;
}

export const useFetchSelectedLocationITApplications = (locationId?: string) => {
  const { data, loading, error, refetch } = useQuery<UseFetchSelectedLocationITApplicationsResult<ITApplication>>(FETCH_LOCATION_SELECTED_IT_APPS, {
    variables: {
      data: locationId,
    },
  });

  return {
    data: data,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook to handle the confirmation flow for the RemoveITApplicationModal.
 * Shows a confirmation modal when removing IT applications that have been affected.
 */
export const useRemoveITApplicationConfirmation = ( applications: ITApplication[] ) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [affectedApplications, setAffectedApplications] =
    useState<AffectedApplication[]>([]);

  // Callback to execute when user confirms the removal
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);

  /**
   * Checks if removing IT applications would affect any applications.
   * If yes, shows the confirmation modal. If no, proceeds with the update immediately.
   */
  const handleBeforeUpdate = (
    appsBeingRemoved: ITApplicationToRemove,
    onProceed: () => void,
  ) => {
    // Check if removing IT applications would affect any applications
    const { hasAffectedApplications } = checkForAffectedITApplications(
      applications,
      appsBeingRemoved,
    );

    if (hasAffectedApplications) {
      // Get the affected applications that have been updated
      const affectedApplications = applications.filter((application) => {
        // Check if this application's IT application is being removed
        const isBeingRemoved = appsBeingRemoved.some(
          (item) => item.uuid === application.uuid,
        );

        // Check if the application has been affected (has a non-null percentage and is greater than 0)
        const hasBeenAffected = application.percentage !== null && application.percentage !== undefined && application.percentage > 0;

        // Include in result if both conditions are true: IT app is being removed AND application was updated
        return isBeingRemoved && hasBeenAffected;
      });

      // Transform the affected applications into the format needed for the modal
      const uniqueAffectedApps = new Map<
        string,
        AffectedApplication
      >();
      affectedApplications.forEach((application) => {
        // Ensure workaround.itApplicationId is not null or undefined before using it as a map key
        if (
          application.uuid &&
          !uniqueAffectedApps.has(application.uuid)
        ) {
          uniqueAffectedApps.set(application.uuid, {
            name:
              application.application ||
              `IT Application ${application.uuid}`
          });
        }
      });
      const transformedWorkarounds = Array.from(uniqueAffectedApps.values());

      setAffectedApplications(transformedWorkarounds);
      setOnConfirm(() => onProceed);
      setModalOpen(true);
    } else {
      onProceed();
    }
  };

  /**
   * Called when user clicks "Remove Applications" in the modal.
   * Executes the stored proceed callback to complete the removal.
   */
  const handleConfirm = () => {
    onConfirm?.(); // Execute the proceed callback if it exists
    setModalOpen(false);
    setOnConfirm(null);
  };

  /**
   * Called when user clicks "Cancel" in the modal.
   * Closes the modal without executing the proceed callback.
   */
  const handleCancel = () => {
    setModalOpen(false);
    setOnConfirm(null);
  };

  return {
    isModalOpen,
    handleBeforeUpdate,
    affectedApplications,
    handleConfirm,
    handleCancel,
  };
};

type AffectedApplicationWarningResult = {
  hasAffectedApplications: boolean;
  count: number;
};


/**
 * Checks if removing IT applications would result in losing updated applications
 * @param applications - Array of current applications
 * @param appsBeingRemoved - Array of IT applications that are being removed
 * @returns Object containing whether there are updated applications being removed and the count
 */
export const checkForAffectedITApplications = (
  applications: ITApplication[],
  appsBeingRemoved: { uuid: string }[],
): AffectedApplicationWarningResult => {
  // Convert the list of apps being removed into a Set for faster lookups
  const removedAppIds = new Set(appsBeingRemoved.map((item) => item.uuid));

  // Find applications for items being removed that have been updated
  const affectedApplications = applications.filter(
    (application) =>
      removedAppIds.has(application.uuid) && // Item is being removed
    (application.percentage !== null && application.percentage !== undefined && application.percentage > 0), // Application has been updated
  );

  return {
    hasAffectedApplications: affectedApplications.length > 0,
    count: affectedApplications.length,
  };
};